import { AddSharp, Search } from "@mui/icons-material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { QrCodeItem } from "../../../@types/DRO/SalesOrderResponse";
import { SalesOrderApi } from "../../../api/OrderManagement/SalesOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";

const CustomerTemplate = () => {
  const columns = ["id", "qrCode"];
  const columnHeaders = ["Customer ID", "QR Code"];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const { setLoading } = useLoading();
  const [customers, setCustomers] = useState<QrCodeItem[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newQrCode, setNewQrCode] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [addLoading, setAddLoading] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const fetchCustomers = useCallback(
    async (resetPage = false) => {
      setLoading(true);
      try {
        const response =
          await SalesOrderApi.api.getAllCustomerListForSalesOrder(
            resetPage ? 0 : page,
            rowsPerPage,
            searchQuery
          );
        setCustomers(response.data.data);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error("Error fetching customers", error);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchQuery]
  );

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar - Fixed width */}
      <div className="w-64 flex-shrink-0 bg-white shadow-md">
        <SideBarNavigation />
      </div>

      {/* Main content area - Takes remaining space */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto bg-[#F8FAFC]">
          <div className="p-6">
            <h1 className="text-3xl font-semibold mb-6 text-left">
              Customer Details
            </h1>

            {/* Search and Add Button Bar */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <TextField
                  placeholder="Search by QR code or ID"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  size="small"
                  sx={{
                    width: 300,
                    bgcolor: "white",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e5e7eb",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search sx={{ color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <Button
                variant="contained"
                startIcon={<AddSharp />}
                sx={{
                  textTransform: "none",
                  bgcolor: "#171F21",
                  "&:hover": { bgcolor: "#0077F" },
                  fontSize: "16px",
                  borderRadius: "30px",
                  px: 3,
                  py: 1,
                  boxShadow: 2,
                }}
                onClick={() => setAddModalOpen(true)}>
                Add Customer
              </Button>
            </div>

            {/* Table Container with horizontal scroll */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] bg-white">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="left-0 z-10 bg-gray-50 w-16 px-4 py-3 text-left align-middle text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      {columnHeaders.map((header, index) => (
                        <th
                          key={index}
                          className="px-4 py-3 text-left align-middle text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {customers.length > 0 ? (
                      customers.map((row, rowIndex) => (
                        <tr
                          key={row.id}
                          className="hover:bg-gray-50 transition-colors">
                          <td className="left-0 z-10 bg-white px-4 py-4 text-sm text-gray-900 text-left align-middle">
                            {page * rowsPerPage + rowIndex + 1}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 text-left align-middle whitespace-nowrap">
                            {row.id}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 text-left align-middle whitespace-nowrap">
                            {row.qrCode}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={columnHeaders.length + 1}
                          className="px-4 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <svg
                              className="w-12 h-12 text-gray-300 mb-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                              />
                            </svg>
                            <p className="text-lg font-medium text-gray-900 mb-1">
                              No customers found
                            </p>
                            <p className="text-gray-500">
                              Try adjusting your search or filter criteria
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                <div className="flex items-center space-x-2">
                  {/* First Page Button */}
                  <button
                    onClick={() => setPage(0)}
                    disabled={page === 0}
                    className={`px-3 py-2 text-xs font-medium rounded-md border ${
                      page === 0
                        ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
                    }`}>
                    First
                  </button>

                  {/* Previous Button */}
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                    disabled={page === 0}
                    className={`p-2 rounded-md border ${
                      page === 0
                        ? "border-gray-200 text-gray-400 cursor-not-allowed"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {/* Next Button */}
                  <button
                    onClick={() =>
                      setPage((prev) => Math.min(prev + 1, totalPages - 1))
                    }
                    disabled={page === totalPages - 1}
                    className={`p-2 rounded-md border ${
                      page === totalPages - 1
                        ? "border-gray-200 text-gray-400 cursor-not-allowed"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  {/* Last Page Button */}
                  <button
                    onClick={() => setPage(totalPages - 1)}
                    disabled={page === totalPages - 1}
                    className={`px-3 py-2 text-xs font-medium rounded-md border ${
                      page === totalPages - 1
                        ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
                    }`}>
                    Last
                  </button>
                </div>

                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-700">
                    Page {page + 1} of {totalPages}
                  </span>
                </div>
              </div>
            )}

            <Dialog
              open={addModalOpen}
              onClose={() => setAddModalOpen(false)}
              PaperProps={{
                sx: {
                  borderRadius: 5,
                  p: 0,
                  minWidth: 420,
                  boxShadow: 24,
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)",
                  overflow: "visible",
                },
              }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pt: 4,
                  pb: 0,
                  px: 4,
                }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #e0e7ef 0%, #cbd5e1 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 2,
                    mb: 2,
                  }}>
                  <PersonAddAlt1Icon sx={{ fontSize: 36, color: "#64748b" }} />
                </Box>
                <DialogTitle
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.5rem",
                    color: "#171F21",
                    pb: 0,
                    textAlign: "center",
                    letterSpacing: 1,
                    width: "100%",
                  }}>
                  Add New Customer
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      color: "#64748b",
                      fontSize: "1rem",
                      mt: 1,
                    }}>
                    Enter customer details below
                  </Typography>
                </DialogTitle>
                <Box sx={{ width: "100%", mt: 2, mb: 1 }}>
                  <Box
                    sx={{ borderBottom: "1px solid #e2e8f0", width: "100%" }}
                  />
                </Box>
              </Box>
              <DialogContent sx={{ pt: 0, pb: 0, px: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 1,
                  }}>
                  <TextField
                    label="QR Code"
                    type="text"
                    fullWidth
                    value={newQrCode}
                    onChange={(e) => setNewQrCode(e.target.value)}
                    disabled={addLoading}
                    variant="outlined"
                    sx={{
                      background: "#fff",
                      borderRadius: 2,
                    }}
                    InputLabelProps={{
                      style: { fontWeight: 500, color: "#64748b" },
                    }}
                  />
                  <TextField
                    label="Address"
                    type="text"
                    fullWidth
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    disabled={addLoading}
                    variant="outlined"
                    sx={{
                      background: "#fff",
                      borderRadius: 2,
                    }}
                    InputLabelProps={{
                      style: { fontWeight: 500, color: "#64748b" },
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions
                sx={{
                  justifyContent: "space-between",
                  px: 4,
                  pb: 3,
                  pt: 2,
                  mt: 1,
                }}>
                <Button
                  onClick={() => setAddModalOpen(false)}
                  disabled={addLoading}
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    fontWeight: 500,
                    color: "#334155",
                    bgcolor: "#f3f4f6",
                    border: "1px solid #cbd5e1",
                    "&:hover": {
                      bgcolor: "#e2e8f0",
                      color: "#1e293b",
                      border: "1.5px solid #94a3b8",
                    },
                    boxShadow: 0,
                    transition: "all 0.2s",
                  }}>
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    setAddLoading(true);
                    try {
                      await SalesOrderApi.api.createCustomerForSalesOrder({
                        qrCode: newQrCode,
                        address: newAddress,
                      });
                      setSnackbar({
                        open: true,
                        message: "Customer added successfully!",
                        severity: "success",
                      });
                      setAddModalOpen(false);
                      setNewQrCode("");
                      setNewAddress("");
                      fetchCustomers(true);
                    } catch (error) {
                      setSnackbar({
                        open: true,
                        message: "Failed to add customer",
                        severity: "error",
                      });
                    } finally {
                      setAddLoading(false);
                    }
                  }}
                  disabled={!newQrCode || !newAddress || addLoading}
                  variant="contained"
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    bgcolor: "#171F21",
                    color: "#fff",
                    boxShadow: 2,
                    "&:hover": { bgcolor: "#0077F" },
                  }}>
                  Add
                </Button>
              </DialogActions>
            </Dialog>
            <Snackbar
              open={snackbar.open}
              autoHideDuration={3000}
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
              <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
                {snackbar.message}
              </Alert>
            </Snackbar>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerTemplate;
